/**
 * <careers-tabs> - vertical tab list (desktop) / custom dropdown (mobile) that filters a single
 * shared job list on the right. Scoped entirely to its own element, so multiple instances of the
 * careers-tabs section can coexist on one page without colliding.
 */
class CareersTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    this.dropdown = this.querySelector('.careers-tabs__dropdown');
    this.dropdownToggle = this.querySelector('.careers-tabs__dropdown-toggle');
    this.dropdownValue = this.querySelector('.careers-tabs__dropdown-value');
    this.dropdownLabel = this.querySelector('.careers-tabs__dropdown-label');
    this.dropdownList = this.querySelector('.careers-tabs__dropdown-list');
    this.dropdownOptions = Array.from(this.querySelectorAll('.careers-tabs__dropdown-option'));

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.selectIndex(index));
    });

    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.dropdownToggle) {
      this.dropdownToggle.addEventListener('click', () => this.toggleDropdown());
    }

    this.dropdownOptions.forEach((option, index) => {
      option.addEventListener('click', () => {
        this.selectIndex(index);
        this.closeDropdown();
        this.dropdownToggle.focus();
      });
    });

    // Each job's trigger button sits right next to a hidden <template> holding that job's modal
    // content (see snippets/careers-job-list.liquid). Populating the shared <x-modal> here, before
    // the click bubbles up to the <x-modal>'s own aria-controls listener (attached on
    // document.body), means the modal already has the right content by the time it opens.
    this.jobTriggers = Array.from(this.querySelectorAll('[data-careers-job-trigger]'));
    this.jobTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.populateJobModal(trigger));
    });

    if (this.dropdown) {
      this.dropdown.addEventListener('keydown', this.onDropdownKeydown.bind(this));
      document.addEventListener('click', (event) => {
        if (!this.dropdown.contains(event.target)) {
          this.closeDropdown();
        }
      });
    }

    // Keep in sync with the theme editor: selecting a "Job function" block should reveal its
    // (possibly hidden) panel, the same way the theme's built-in <x-tabs> component does.
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.tabs.indexOf(event.target);

      if (index !== -1) {
        this.selectIndex(index);
      }
    });
  }

  selectIndex(index) {
    if (index < 0 || index >= this.tabs.length) {
      return;
    }

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.classList.toggle('is-selected', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    this.dropdownOptions.forEach((option, i) => {
      const selected = i === index;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    if (this.dropdownValue && this.dropdownOptions[index]) {
      this.dropdownValue.textContent = this.dropdownOptions[index].firstChild.textContent.trim();
    }
  }

  populateJobModal(trigger) {
    const modal = document.getElementById(trigger.getAttribute('aria-controls'));
    const data = trigger.nextElementSibling;

    if (!modal || !data || data.tagName !== 'TEMPLATE') {
      return;
    }

    const body = modal.querySelector('.careers-job-modal__body');
    const applyLink = modal.querySelector('.careers-job-modal__apply .button');

    if (body) {
      body.replaceChildren(data.content.cloneNode(true));
    }

    if (applyLink && trigger.dataset.jobApplyLink) {
      applyLink.href = trigger.dataset.jobApplyLink;
    }

    modal.querySelector('.careers-job-modal__scroll')?.scrollTo(0, 0);
  }

  toggleDropdown() {
    if (!this.dropdown) {
      return;
    }

    const isOpen = this.dropdownToggle.getAttribute('aria-expanded') === 'true';
    isOpen ? this.closeDropdown() : this.openDropdown();
  }

  openDropdown() {
    if (!this.dropdown) {
      return;
    }

    this.dropdownToggle.setAttribute('aria-expanded', 'true');
    this.dropdownList.hidden = false;
    this.dropdownValue.hidden = true;
    this.dropdownLabel.hidden = false;
    this.dropdown.classList.add('is-open');

    const selected = this.dropdownOptions.find((option) => option.classList.contains('is-selected'));
    (selected || this.dropdownOptions[0])?.focus();
  }

  closeDropdown() {
    if (!this.dropdown) {
      return;
    }

    this.dropdownToggle.setAttribute('aria-expanded', 'false');
    this.dropdownList.hidden = true;
    this.dropdownValue.hidden = false;
    this.dropdownLabel.hidden = true;
    this.dropdown.classList.remove('is-open');
  }

  onDropdownKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDropdown();
      this.dropdownToggle.focus();
      return;
    }

    if (!['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
      return;
    }

    const active = this.dropdownOptions.indexOf(document.activeElement);

    if (active === -1) {
      return;
    }

    event.preventDefault();

    if (event.key === 'Enter' || event.key === ' ') {
      this.selectIndex(active);
      this.closeDropdown();
      this.dropdownToggle.focus();
      return;
    }

    let nextIndex = active;

    if (event.key === 'ArrowUp') {
      nextIndex = (active - 1 + this.dropdownOptions.length) % this.dropdownOptions.length;
    } else if (event.key === 'ArrowDown') {
      nextIndex = (active + 1) % this.dropdownOptions.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.dropdownOptions.length - 1;
    }

    this.dropdownOptions[nextIndex].focus();
  }

  onKeydown(event) {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const active = this.tabs.indexOf(document.activeElement);

    if (active === -1) {
      return;
    }

    event.preventDefault();

    let nextIndex = active;

    if (event.key === 'ArrowUp') {
      nextIndex = (active - 1 + this.tabs.length) % this.tabs.length;
    } else if (event.key === 'ArrowDown') {
      nextIndex = (active + 1) % this.tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.tabs.length - 1;
    }

    this.selectIndex(nextIndex);
    this.tabs[nextIndex].focus();
  }
}

if (!window.customElements.get('careers-tabs')) {
  window.customElements.define('careers-tabs', CareersTabs);
}
